import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Scissors, Search, RefreshCw, ExternalLink, Loader2, ShieldOff, ShieldCheck,
  CheckCircle2, XCircle, Banknote, Eye, DollarSign, Users, Hourglass,
  ChevronDown, Shield, Save, User,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import AdminClipperProfileDialog from './AdminClipperProfileDialog';
import {
  adminListClippers, adminBlockClipper,
  adminListClips, adminRescrapeClip, adminSetClipStatus, adminSetClipViews, adminReviewClip,
  adminListClipWithdrawals, adminDecideClipWithdrawal,
  adminGetSettings, adminUpdateSettings,
  apiErrMsg,
} from '../../lib/api';

function fmt$(n) { return `$${Number(n || 0).toFixed(2)}`; }
function fmtN(n) { return Number(n || 0).toLocaleString(); }
function shortDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function AdminClippersTab() {
  const [tab, setTab] = useState('clippers');
  const [clippers, setClippers] = useState([]);
  const [clips, setClips] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [search, setSearch] = useState('');
  const [withdrawFilter, setWithdrawFilter] = useState('');
  const [clipPlatformFilter, setClipPlatformFilter] = useState('');
  const [clipFlagFilter, setClipFlagFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cl, w] = await Promise.all([
        adminListClippers(search, 200),
        adminListClips({ platform: clipPlatformFilter, flag: clipFlagFilter, limit: 300 }),
        adminListClipWithdrawals(withdrawFilter, 300),
      ]);
      setClippers(c.items || []);
      setClips(cl.items || []);
      setWithdrawals(w.items || []);
    } catch (e) {
      toast.error(apiErrMsg(e, "Failed to load"));
    } finally { setLoading(false); }
  }, [search, clipPlatformFilter, clipFlagFilter, withdrawFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const stats = useMemo(() => {
    const pendingW = withdrawals.filter((w) => w.status === 'pending').length;
    const pendingTotal = withdrawals.filter((w) => w.status === 'pending').reduce((a, w) => a + w.amount_usd, 0);
    const totalEarned = clippers.reduce((a, c) => a + c.total_earned_usd, 0);
    const totalViews = clippers.reduce((a, c) => a + c.total_views, 0);
    return { pendingW, pendingTotal, totalEarned, totalViews, totalClippers: clippers.length, totalClips: clips.length };
  }, [withdrawals, clippers, clips]);

  return (
    <div className="space-y-5" data-testid="admin-clippers-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl flex items-center gap-2"><Scissors className="h-5 w-5 text-[hsl(43_92%_56%)]" /> Clip Program</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Manage clippers, clips, and withdrawals.</p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" className="h-9 border-white/10 bg-white/[0.02] gap-1.5" data-testid="clippers-refresh">
          <RefreshCw className={'h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} /> Refresh
        </Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat Icon={Users} label="Clippers" value={stats.totalClippers} />
        <Stat Icon={Scissors} label="Total clips" value={stats.totalClips} />
        <Stat Icon={Eye} label="Total views" value={fmtN(stats.totalViews)} />
        <Stat Icon={DollarSign} label="Total earned" value={fmt$(stats.totalEarned)} />
        <Stat Icon={Hourglass} label={`Pending payouts (${stats.pendingW})`} value={fmt$(stats.pendingTotal)} gold />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/[0.02] border border-white/10 p-1 gap-1">
          <TabsTrigger value="clippers" data-testid="admin-clip-tab-clippers" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
            Clippers ({clippers.length})
          </TabsTrigger>
          <TabsTrigger value="clips" data-testid="admin-clip-tab-clips" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
            Clips ({clips.length})
          </TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="admin-clip-tab-withdrawals" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
            Withdrawals ({withdrawals.length})
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-clip-tab-settings" className="data-[state=active]:bg-[rgba(245,185,66,0.10)] data-[state=active]:text-[hsl(43_92%_72%)]">
            Anti-fraud
          </TabsTrigger>
        </TabsList>

        {/* CLIPPERS */}
        <TabsContent value="clippers" className="mt-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…"
              className="pl-9 bg-white/[0.02] border-white/10" data-testid="clippers-search" />
          </div>
          <div className="grid gap-2">
            {clippers.length === 0 ? (
              <Empty label="No clippers yet" />
            ) : clippers.map((c) => (
              <Card key={c.id} className="card-surface" data-testid={`clipper-row-${c.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display text-base flex items-center gap-2">
                      {c.name || '(unnamed)'}
                      {c.blocked && <Badge variant="outline" className="border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] text-[10px] uppercase tracking-[0.18em]">Blocked</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{c.email} · {c.country || '—'} · {c.telegram_handle || '—'}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Joined: {shortDate(c.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-5 flex-wrap">
                    <Mini label="Clips" value={c.clip_count} />
                    <Mini label="Views" value={fmtN(c.total_views)} />
                    <Mini label="Earned" value={fmt$(c.total_earned_usd)} />
                    <Mini label="Pending" value={fmt$(c.pending_balance_usd)} gold />
                    <Mini label="Paid out" value={fmt$(c.total_paid_out_usd)} />
                    <Button
                      size="sm" variant="outline"
                      className="h-8 border-white/10 bg-white/[0.02] gap-1.5"
                      onClick={() => setProfileId(c.id)}
                      data-testid={`clipper-view-profile-${c.id}`}
                    >
                      <User className="h-3.5 w-3.5" /> View profile
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className={'h-8 border-white/10 bg-white/[0.02] gap-1.5 ' + (c.blocked ? 'text-[hsl(140_70%_60%)]' : 'text-[hsl(0_72%_70%)]')}
                      onClick={async () => {
                        try {
                          await adminBlockClipper(c.id, !c.blocked);
                          toast.success(c.blocked ? 'Clipper unblocked' : 'Clipper blocked');
                          refresh();
                        } catch (e) { toast.error(apiErrMsg(e, "Failed")); }
                      }}
                      data-testid={`clipper-block-${c.id}`}
                    >
                      {c.blocked ? <><ShieldCheck className="h-3.5 w-3.5" /> Unblock</> : <><ShieldOff className="h-3.5 w-3.5" /> Block</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* CLIPS */}
        <TabsContent value="clips" className="mt-4 space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            <select value={clipPlatformFilter} onChange={(e) => setClipPlatformFilter(e.target.value)}
              className="h-9 bg-white/[0.02] border border-white/10 rounded-md px-3 text-sm" data-testid="clip-platform-filter">
              <option value="">All platforms</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
            </select>
            <select value={clipFlagFilter} onChange={(e) => setClipFlagFilter(e.target.value)}
              className="h-9 bg-white/[0.02] border border-white/10 rounded-md px-3 text-sm" data-testid="clip-flag-filter">
              <option value="">All engagement</option>
              <option value="suspicious">🚨 Suspicious (soft + hard)</option>
              <option value="hard">🔴 Hard-flagged (auto-hold)</option>
              <option value="soft">🟡 Soft warnings</option>
              <option value="ok">✅ Healthy</option>
            </select>
            <span className="text-xs text-muted-foreground">{clips.length} clips</span>
            {clips.some((c) => c.engagement_flag === 'hard') && (
              <span className="text-xs text-[hsl(0_72%_72%)]">
                • {clips.filter((c) => c.engagement_flag === 'hard').length} on hold
              </span>
            )}
          </div>
          <div className="grid gap-2">
            {clips.length === 0 ? (
              <Empty label="No clips submitted yet" />
            ) : clips.map((c) => (
              <AdminClipRow key={c.id} clip={c} refresh={refresh} onViewOwner={setProfileId} />
            ))}
          </div>
        </TabsContent>

        {/* WITHDRAWALS */}
        <TabsContent value="withdrawals" className="mt-4 space-y-3">
          <div className="flex gap-2 items-center">
            <select value={withdrawFilter} onChange={(e) => setWithdrawFilter(e.target.value)}
              className="h-9 bg-white/[0.02] border border-white/10 rounded-md px-3 text-sm" data-testid="withdraw-filter">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
            <span className="text-xs text-muted-foreground">{withdrawals.length} requests</span>
          </div>
          <div className="grid gap-2">
            {withdrawals.length === 0 ? (
              <Empty label="No withdrawal requests" />
            ) : withdrawals.map((w) => (
              <AdminWithdrawalRow key={w.id} w={w} refresh={refresh} />
            ))}
          </div>
        </TabsContent>

        {/* SETTINGS — Anti-fraud */}
        <TabsContent value="settings" className="mt-4 space-y-3">
          <AntiFraudSettings />
        </TabsContent>
      </Tabs>

      <AdminClipperProfileDialog
        open={!!profileId}
        onOpenChange={(v) => { if (!v) setProfileId(null); }}
        clipperId={profileId}
        onChanged={refresh}
      />
    </div>
  );
}

function AntiFraudSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [requireHandle, setRequireHandle] = useState(true);
  const [tokensText, setTokensText] = useState('');
  const [platTokens, setPlatTokens] = useState({ tiktok: '', instagram: '', facebook: '' });
  const [softPct, setSoftPct] = useState(0.5);
  const [hardPct, setHardPct] = useState(0.15);
  const [minViews, setMinViews] = useState(500);
  const [spikeMult, setSpikeMult] = useState(15);
  const [holdFlagged, setHoldFlagged] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await adminGetSettings();
      setSettings(s);
      setRequireHandle(s.clip_require_handle_match !== false);
      const tk = s.clip_required_attribution_tokens || [];
      setTokensText(tk.join(', '));
      const pa = s.clip_platform_attribution || {};
      setPlatTokens({
        tiktok: (pa.tiktok || []).join(', '),
        instagram: (pa.instagram || []).join(', '),
        facebook: (pa.facebook || []).join(', '),
      });
      setSoftPct(Number(s.clip_engagement_soft_threshold_pct ?? 0.5));
      setHardPct(Number(s.clip_engagement_hard_threshold_pct ?? 0.15));
      setMinViews(Number(s.clip_engagement_min_views ?? 500));
      setSpikeMult(Number(s.clip_velocity_spike_multiplier ?? 15));
      setHoldFlagged(s.clip_hold_flagged_earnings !== false);
    } catch (e) { toast.error(apiErrMsg(e, "Failed to load")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function parseTokens(raw) {
    return (raw || '').split(/[\n,]+/).map((t) => t.trim()).filter(Boolean).slice(0, 12);
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = {
        ...settings,
        clip_require_handle_match: !!requireHandle,
        clip_required_attribution_tokens: parseTokens(tokensText),
        clip_platform_attribution: {
          tiktok: parseTokens(platTokens.tiktok),
          instagram: parseTokens(platTokens.instagram),
          facebook: parseTokens(platTokens.facebook),
        },
        clip_engagement_soft_threshold_pct: Number(softPct),
        clip_engagement_hard_threshold_pct: Number(hardPct),
        clip_engagement_min_views: Math.max(0, Math.floor(Number(minViews) || 0)),
        clip_velocity_spike_multiplier: Number(spikeMult),
        clip_hold_flagged_earnings: !!holdFlagged,
      };
      await adminUpdateSettings(payload);
      toast.success('Anti-fraud settings saved');
      load();
    } catch (e) { toast.error(apiErrMsg(e, "Save failed")); }
    finally { setSaving(false); }
  }

  if (loading) {
    return (
      <Card className="card-surface"><CardContent className="p-8 text-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" /> Loading…
      </CardContent></Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="anti-fraud-settings">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Clip verification rules
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Controls what checks run at clip submission and periodic scrape. Fails fast with a helpful error message.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Handle match */}
        <div className="flex items-start justify-between gap-4 rounded-md border border-white/10 bg-white/[0.02] p-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold">Require handle match</div>
            <div className="text-xs text-muted-foreground mt-1">
              Video&apos;s <span className="font-mono">ownerUsername</span> must match one of the clipper&apos;s
              registered social handles for that platform. Recommended: ON.
            </div>
          </div>
          <Switch checked={requireHandle} onCheckedChange={setRequireHandle} data-testid="toggle-require-handle" />
        </div>

        {/* Common attribution tokens */}
        <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.02] p-4">
          <Label className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)]">
            Common tokens (caption must include ANY one)
          </Label>
          <Input
            value={tokensText}
            onChange={(e) => setTokensText(e.target.value)}
            placeholder="#alveetech, #alveefx, #traderalvee"
            data-testid="attribution-tokens"
            className="font-mono text-xs"
          />
          <div className="text-[11px] text-muted-foreground">Comma-separated. Applies to all platforms. Leave empty to disable.</div>
        </div>

        {/* Platform-specific attribution tokens */}
        <div className="space-y-3 rounded-md border border-white/10 bg-white/[0.02] p-4">
          <Label className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)]">
            Platform-specific tokens (caption must include ALL for that platform)
          </Label>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">TikTok</Label>
              <Input value={platTokens.tiktok} onChange={(e) => setPlatTokens((p) => ({ ...p, tiktok: e.target.value }))}
                placeholder="@alveefxking" data-testid="plat-tokens-tiktok" className="font-mono text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Instagram</Label>
              <Input value={platTokens.instagram} onChange={(e) => setPlatTokens((p) => ({ ...p, instagram: e.target.value }))}
                placeholder="@traderalvee" data-testid="plat-tokens-instagram" className="font-mono text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Facebook</Label>
              <Input value={platTokens.facebook} onChange={(e) => setPlatTokens((p) => ({ ...p, facebook: e.target.value }))}
                placeholder="https://t.me/alveefx" data-testid="plat-tokens-facebook" className="font-mono text-xs" />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">Comma-separated per platform. Every listed token must appear in the caption.</div>
        </div>

        {/* SMM / Bot detection thresholds */}
        <div className="space-y-3 rounded-md border border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.04)] p-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.22em] text-[hsl(0_72%_75%)]">
              🚨 SMM / Bot-view detection
            </Label>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Auto-flag clips with abnormally low likes:views ratio (typical signature of purchased fake views).
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Soft flag (%)</Label>
              <Input type="number" step="0.01" min="0" value={softPct} onChange={(e) => setSoftPct(e.target.value)}
                data-testid="soft-threshold" className="font-mono text-xs" />
              <div className="text-[10px] text-muted-foreground mt-1">Like rate below this = yellow warning.</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Hard flag (%)</Label>
              <Input type="number" step="0.01" min="0" value={hardPct} onChange={(e) => setHardPct(e.target.value)}
                data-testid="hard-threshold" className="font-mono text-xs" />
              <div className="text-[10px] text-muted-foreground mt-1">Like rate below this = auto-hold earnings.</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Min views to check</Label>
              <Input type="number" step="1" min="0" value={minViews} onChange={(e) => setMinViews(e.target.value)}
                data-testid="min-views" className="font-mono text-xs" />
              <div className="text-[10px] text-muted-foreground mt-1">Skip check on clips smaller than this.</div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Velocity spike (× baseline)</Label>
              <Input type="number" step="0.5" min="2" value={spikeMult} onChange={(e) => setSpikeMult(e.target.value)}
                data-testid="spike-mult" className="font-mono text-xs" />
              <div className="text-[10px] text-muted-foreground mt-1">Flag if today&apos;s views jump this many × the recent daily average.</div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.02] p-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold">Auto-hold flagged earnings</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Exclude hard-flagged clip earnings from withdrawal until admin review.</div>
              </div>
              <Switch checked={holdFlagged} onCheckedChange={setHoldFlagged} data-testid="toggle-hold" />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button onClick={save} disabled={saving} className="cta-sheen border-0 gap-2" data-testid="save-anti-fraud">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Empty({ label }) {
  return (
    <Card className="card-surface">
      <CardContent className="p-10 text-center text-muted-foreground text-sm">{label}</CardContent>
    </Card>
  );
}

function Stat({ Icon, label, value, gold }) {
  return (
    <Card className={'card-surface ' + (gold ? 'border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.04)]' : '')}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <Icon className={'h-3.5 w-3.5 ' + (gold ? 'text-[hsl(43_92%_56%)]' : '')} /> {label}
        </div>
        <div className={'font-display text-xl mt-1 tabular-nums ' + (gold ? 'text-[hsl(43_92%_56%)]' : 'text-white')}>{value}</div>
      </CardContent>
    </Card>
  );
}

function Mini({ label, value, gold }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={'font-display tabular-nums ' + (gold ? 'text-[hsl(43_92%_56%)]' : 'text-white')}>{value}</div>
    </div>
  );
}

function AdminClipRow({ clip, refresh, onViewOwner }) {
  const [busy, setBusy] = useState(false);
  async function rescrape() {
    setBusy(true);
    try {
      const r = await adminRescrapeClip(clip.id);
      toast.success(r.scraped_views != null ? `Got ${r.scraped_views} views` : 'No views detected');
      refresh();
    } catch (e) { toast.error(apiErrMsg(e, "Failed")); }
    finally { setBusy(false); }
  }
  async function setStatus(newStatus) {
    try {
      await adminSetClipStatus(clip.id, newStatus);
      toast.success(`Marked ${newStatus}`);
      refresh();
    } catch (e) { toast.error(apiErrMsg(e, "Failed")); }
  }
  async function manualSetViews() {
    const cur = Number(clip.current_views || 0);
    const raw = window.prompt(
      `Manually set views for this ${clip.platform} clip.\n\nUse this when the platform (e.g. restricted Instagram reels) blocks auto-scraping.\nEarnings will be recomputed automatically.`,
      String(cur)
    );
    if (raw == null) return;
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n) || n < 0) { toast.error("Enter a valid non-negative integer"); return; }
    try {
      await adminSetClipViews(clip.id, n);
      toast.success(`Views set to ${n.toLocaleString()}`);
      refresh();
    } catch (e) { toast.error(apiErrMsg(e, "Failed to set views")); }
  }
  async function reviewClip(decision) {
    const label = decision === 'organic' ? 'ORGANIC (release held earnings)' : 'FRAUD (zero out earnings + block)';
    const notes = window.prompt(
      `Mark this clip as ${label}?\n\nOptional notes for audit trail:`,
      ''
    );
    if (notes == null) return; // cancelled
    try {
      await adminReviewClip(clip.id, decision, notes);
      toast.success(decision === 'organic' ? 'Marked as organic — held earnings released' : 'Marked as fraud — earnings zeroed out');
      refresh();
    } catch (e) { toast.error(apiErrMsg(e, "Review failed")); }
  }

  const flag = clip.engagement_flag || 'ok';
  const review = clip.engagement_review || '';
  const flagStyle = flag === 'hard'
    ? 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] bg-[rgba(225,29,46,0.08)]'
    : flag === 'soft'
    ? 'border-[rgba(245,185,66,0.55)] text-[hsl(43_92%_72%)] bg-[rgba(245,185,66,0.08)]'
    : 'border-white/10';
  const flagLabel = flag === 'hard' ? '🔴 SMM/BOT (held)' : flag === 'soft' ? '🟡 low engagement' : 'healthy';

  return (
    <Card className="card-surface" data-testid={`admin-clip-row-${clip.id}`}>
      <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/10">{clip.platform}</Badge>
            <Badge variant="outline" className={'text-[10px] uppercase tracking-[0.18em] ' + (clip.status === 'active' ? 'border-white/10' : 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]')}>{clip.status}</Badge>
            {flag !== 'ok' && (
              <Badge variant="outline" className={'text-[10px] uppercase tracking-[0.18em] ' + flagStyle} data-testid={`flag-${clip.id}`}>
                {flagLabel}
              </Badge>
            )}
            {review === 'confirmed_organic' && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-[rgba(46,204,113,0.55)] text-[hsl(140_60%_70%)] bg-[rgba(46,204,113,0.08)]">
                ✓ organic (reviewed)
              </Badge>
            )}
            <a href={clip.url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(43_92%_72%)] hover:underline truncate max-w-[400px] inline-flex items-center gap-1">
              {clip.url} <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </div>
          {clip.title && <div className="text-xs text-muted-foreground truncate mt-1">{clip.title}</div>}
          {clip.verified_owner && (
            <div className="text-[10px] text-[hsl(140_50%_70%)] mt-1 truncate">
              ✓ Verified owner: @{clip.verified_owner}
              {clip.verified_caption && <span className="text-muted-foreground"> · {clip.verified_caption.slice(0, 80)}{clip.verified_caption.length > 80 ? '…' : ''}</span>}
            </div>
          )}
          {(clip.likes_count != null || clip.engagement_like_rate_pct != null) && (
            <div className="text-[10px] text-muted-foreground mt-1">
              Engagement: {fmtN(clip.likes_count || 0)} likes · {fmtN(clip.comments_count || 0)} comments
              {clip.engagement_like_rate_pct != null && (
                <span className={flag !== 'ok' ? ' text-[hsl(43_92%_72%)]' : ''}> · like rate {clip.engagement_like_rate_pct}%</span>
              )}
            </div>
          )}
          {(clip.engagement_reasons || []).length > 0 && (
            <div className="text-[10px] text-[hsl(0_72%_72%)] mt-1">
              ⚠ {clip.engagement_reasons.join(' · ')}
            </div>
          )}
          <div className="text-[10px] text-muted-foreground mt-1">Last scan: {shortDate(clip.last_scraped_at)} {clip.scrape_error && `· ${clip.scrape_error}`}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Mini label="Views" value={fmtN(clip.current_views)} />
          <Mini label="Earned" value={fmt$(clip.earned_usd)} gold />
          {onViewOwner && clip.clipper_id && (
            <Button size="sm" variant="outline" onClick={() => onViewOwner(clip.clipper_id)}
              className="h-8 border-white/10 bg-white/[0.02] gap-1.5" data-testid={`view-owner-${clip.id}`}>
              <User className="h-3.5 w-3.5" /> Owner
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={busy} onClick={rescrape}
            className="h-8 border-white/10 bg-white/[0.02] gap-1.5" data-testid={`rescrape-${clip.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Rescrape
          </Button>
          <Button size="sm" variant="outline" onClick={manualSetViews}
            className="h-8 border-white/10 bg-white/[0.02] gap-1.5" data-testid={`set-views-${clip.id}`}>
            Set Views
          </Button>
          {(flag === 'hard' || flag === 'soft') && review !== 'confirmed_organic' && (
            <Button size="sm" variant="outline" onClick={() => reviewClip('organic')}
              className="h-8 border-[rgba(46,204,113,0.35)] bg-[rgba(46,204,113,0.05)] text-[hsl(140_60%_70%)] gap-1.5"
              data-testid={`mark-organic-${clip.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Mark Organic
            </Button>
          )}
          {flag !== 'ok' && review !== 'confirmed_fraud' && (
            <Button size="sm" variant="outline" onClick={() => reviewClip('fraud')}
              className="h-8 border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.05)] text-[hsl(0_72%_70%)] gap-1.5"
              data-testid={`mark-fraud-${clip.id}`}>
              <XCircle className="h-3.5 w-3.5" /> Mark Fraud
            </Button>
          )}
          <select value={clip.status} onChange={(e) => setStatus(e.target.value)}
            className="h-8 bg-white/[0.02] border border-white/10 rounded-md px-2 text-xs" data-testid={`status-${clip.id}`}>
            <option value="active">active</option>
            <option value="blocked">blocked</option>
            <option value="removed">removed</option>
          </select>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminWithdrawalRow({ w, refresh }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(w.admin_notes || '');
  const [txhash, setTxhash] = useState(w.txhash || '');
  const [open, setOpen] = useState(false);

  async function act(action) {
    setBusy(true);
    try {
      await adminDecideClipWithdrawal(w.id, { action, admin_notes: notes, txhash });
      toast.success(`Marked ${action.replace('_', ' ')}`);
      refresh();
    } catch (e) { toast.error(apiErrMsg(e, "Failed")); }
    finally { setBusy(false); }
  }

  const sm = {
    pending: 'border-white/10 text-muted-foreground',
    approved: 'border-[rgba(108,176,255,0.55)] text-[hsl(212_80%_75%)]',
    paid: 'border-[rgba(70,200,120,0.55)] text-[hsl(140_70%_70%)]',
    rejected: 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]',
  }[w.status] || 'border-white/10';

  return (
    <Card className="card-surface" data-testid={`admin-withdraw-row-${w.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div>
              <div className="font-display text-xl tabular-nums">{fmt$(w.amount_usd)}</div>
              <div className="text-[11px] text-muted-foreground truncate">{w.clipper_email || w.clipper_id}</div>
            </div>
            <Badge variant="outline" className={'text-[10px] uppercase tracking-[0.22em] ' + sm}>{w.status}</Badge>
          </div>
          <div className="text-[10px] text-muted-foreground">{shortDate(w.requested_at)}</div>
        </div>
        <div className="text-[11px] font-mono break-all bg-white/[0.02] border border-white/10 rounded px-3 py-2">
          {w.usdt_bep20_address}
        </div>
        {(w.txhash || w.admin_notes) && (
          <div className="text-[11px] text-muted-foreground">
            {w.txhash && <div>tx: <span className="font-mono break-all">{w.txhash}</span></div>}
            {w.admin_notes && <div>note: {w.admin_notes}</div>}
          </div>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-white inline-flex items-center gap-1">
          <ChevronDown className={'h-3 w-3 transition-transform ' + (open ? 'rotate-180' : '')} /> Actions
        </button>
        {open && w.status !== 'paid' && w.status !== 'rejected' && (
          <div className="border-t border-white/10 pt-3 space-y-2">
            <Input value={txhash} onChange={(e) => setTxhash(e.target.value)} placeholder="Tx hash (optional)" className="bg-white/[0.02] border-white/10 font-mono text-xs" data-testid={`txhash-${w.id}`} />
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (visible to clipper)" className="bg-white/[0.02] border-white/10 text-xs" data-testid={`notes-${w.id}`} />
            <div className="flex items-center gap-2 flex-wrap">
              {w.status === 'pending' && (
                <Button size="sm" disabled={busy} onClick={() => act('approve')}
                  className="cta-sheen border-0 gap-1.5 h-8" data-testid={`approve-${w.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </Button>
              )}
              <Button size="sm" disabled={busy} onClick={() => act('mark_paid')}
                className="border-0 gap-1.5 h-8 bg-[hsl(140_60%_40%)] hover:bg-[hsl(140_60%_44%)]" data-testid={`mark-paid-${w.id}`}>
                <Banknote className="h-3.5 w-3.5" /> Mark Paid
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act('reject')}
                className="h-8 border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] hover:bg-[rgba(225,29,46,0.06)] gap-1.5"
                data-testid={`reject-${w.id}`}>
                <XCircle className="h-3.5 w-3.5" /> Reject
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
